import {Grid, GridItem, VStack} from 'platform/foundation';
import {useDateTimeFormatter} from 'platform/locale';

import {isNil} from 'ramda';

import {PaymentTypeResponseBody} from '@dms/api/core';
import {BaseAuthorizationProfile} from '@dms/api/security';
import {BranchListItemResponseBody} from '@dms/api/shared';
import {GetReceiveNoteResponse, GetWarehousesResponse} from '@dms/api/warehouse';
import i18n from '@dms/i18n';
import {ReadOnlyField} from '@dms/shared-aftersales';

import {EMPTY_PLACEHOLDER, Nullish, parseDate, Unpacked} from 'shared/web';

interface BranchBasedInformationReadOnlyProps {
  receiveNote: GetReceiveNoteResponse | Nullish;
  sourceBranch: BranchListItemResponseBody | Nullish;
  branch: BranchListItemResponseBody | Nullish;
  authorizationProfile: Unpacked<BaseAuthorizationProfile[]> | Nullish;
  warehouse: Unpacked<GetWarehousesResponse> | Nullish;
  paymentType: Unpacked<PaymentTypeResponseBody[]> | Nullish;
}

export function BranchBasedInformationReadOnly(props: BranchBasedInformationReadOnlyProps) {
  const dateTimeFormatter = useDateTimeFormatter();

  const isOpeningBalance = props.receiveNote?.type === 'OPENING_BALANCE';

  const formatToShortDate = (date?: string) => {
    if (isNil(date)) {
      return EMPTY_PLACEHOLDER;
    }
    return dateTimeFormatter('dateShort', parseDate(date));
  };

  return (
    <VStack spacing={4}>
      <Grid columns={4}>
        <ReadOnlyField
          label={i18n.t('entity.warehouse.labels.sourceBranch')}
          value={props.sourceBranch?.marketingName}
        />
        <ReadOnlyField
          label={i18n.t('entity.warehouse.labels.warehouse')}
          value={props.warehouse?.name}
        />
        <ReadOnlyField
          label={i18n.t('entity.warehouse.labels.authorizationProfile')}
          value={props.authorizationProfile?.name}
        />
        <ReadOnlyField
          label={i18n.t('entity.warehouse.labels.number')}
          value={props.receiveNote?.receiveNoteNumber}
        />
      </Grid>
      <Grid columns={4}>
        <ReadOnlyField
          label={i18n.t('entity.warehouse.labels.date')}
          value={formatToShortDate(props.receiveNote?.invoiceIssueDate)}
        />
        <ReadOnlyField
          label={
            isOpeningBalance
              ? i18n.t('entity.warehouse.labels.openingBalanceNo')
              : i18n.t('entity.warehouse.labels.transferOrderNo')
          }
          value={props.receiveNote?.deliveryNote?.deliveryNoteNumber}
        />
        <ReadOnlyField
          label={
            isOpeningBalance
              ? i18n.t('entity.warehouse.labels.openingBalanceDate')
              : i18n.t('entity.warehouse.labels.transferOrderDate')
          }
          value={formatToShortDate(props.receiveNote?.deliveryNote?.deliveryNoteIssuedAt)}
        />
        <ReadOnlyField
          label={i18n.t('entity.warehouse.labels.paymentType')}
          value={props.paymentType?.value}
        />
      </Grid>
      <Grid columns={4}>
        <GridItem span={4}>
          <ReadOnlyField
            label={i18n.t('entity.warehouse.labels.note')}
            value={props.receiveNote?.note}
          />
        </GridItem>
      </Grid>
    </VStack>
  );
}
